import React, { ReactNode, useEffect, useState } from 'react'
import { SelectedTabProvider, Tab, Tabs } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  selectCustomFields,
  selectSaleHasLineItemFulfilment,
  selectServiceFields,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useVdFeatureManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { hasServiceOrder } from '../../../functions/service'

export enum LineItemTab {
  DETAIL = 'detail',
  FULFILMENT = 'fulfilment',
}

interface LineItemTabsProps {
  defaultTab?: LineItemTab
  onChange: (selected: LineItemTab) => void
  children?: ReactNode | undefined
}

export const LineItemTabs: React.FC<LineItemTabsProps> = ({
  defaultTab = LineItemTab.DETAIL,
  onChange,
  children,
}) => {
  const t = useT()
  const [currentTab, setCurrentTab] = useState(defaultTab)
  const vdFeatureManager = useVdFeatureManager()
  const isFulfilmentLifEnabled = vdFeatureManager.get('fulfillment_lif')
  const isFulfilmentLifEnabledB2 = vdFeatureManager.get('fulfillment_lif_b3')
  const hasFulfilment = useAppSelector(selectSaleHasLineItemFulfilment)
  const serviceFields = useAppSelector(selectServiceFields)
  const customFields = useAppSelector(selectCustomFields)
  const isDisplay = isFulfilmentLifEnabled && hasFulfilment

  useEffect(() => {
    if (!isDisplay && currentTab === LineItemTab.FULFILMENT) {
      setCurrentTab(LineItemTab.DETAIL)
    }
  }, [isDisplay, currentTab])

  const displayTabs =
    (isFulfilmentLifEnabled && isFulfilmentLifEnabledB2) || isDisplay

  // We want to hide the fulfilment tab in sales with service orders
  const canDisplayFulfilmentTab =
    displayTabs && !hasServiceOrder(serviceFields, customFields)

  if (!displayTabs) {
    return null
  }

  return (
    <SelectedTabProvider
      defaultTab={defaultTab}
      onChange={string => {
        const selectedTab = string as LineItemTab
        setCurrentTab(selectedTab)
        onChange(selectedTab)
      }}
    >
      <Tabs className="vd-mb3">
        <Tab data-track="details" name={LineItemTab.DETAIL}>
          {t('Details', { _context: 'Line item tabs' })}
        </Tab>
        {canDisplayFulfilmentTab && (
          <Tab data-track="fulfillment" name={LineItemTab.FULFILMENT}>
            {t('Fulfillment', { _context: 'Line item tabs' })}
          </Tab>
        )}
      </Tabs>

      {children}
    </SelectedTabProvider>
  )
}
